// apps/client/src/hooks/office/useRolling3RunWindowOffice.ts
// OFFICE DASHBOARD SPECIFIC - Year-based filtering only (no relative periods)
// Separated from main dashboard hooks for independent evolution
// MIGRATED (2026-07-10): Fetches through the shared dashboard query cache (useDashboardQuery).

import { useMemo, useRef } from 'react';
import { Timestamp } from 'firebase/firestore';
import { useDashboardQuery } from '../useDashboardQuery';
import { formatCompactCurrency } from '../../utils/formatters';

/**
 * Upload aggregate data structure
 */
export interface UploadAggregate {
  uploadId: string;
  companyId: string;
  templateId: string;
  yearMonth: string;
  uploadedAt: Timestamp;
  runNumber: number;
  filename: string;
  upfrontGross: number;
  upfrontAdjustments: number;
  upfrontNet: number;
  trailGross: number;
  trailAdjustments: number;
  trailNet: number;
  totalLoanAmount: number;
  lineOfTrail: number;
  recordCount: number;
  byBroker?: Record<string, {
    brokerDisplay: string;
    brokerId: string;
    upfrontNet: number;
    trailNet: number;
    totalLoanAmount: number;
    lineOfTrail: number;
    recordCount: number;
  }>;
  byOffice: Record<string, {
    upfrontGross: number;
    upfrontAdjustments: number;
    upfrontNet: number;
    trailGross: number;
    trailAdjustments: number;
    trailNet: number;
    totalLoanAmount: number;
    lineOfTrail: number;
    recordCount: number;
  }>;
  processingVersion: number;
}

/**
 * Rolling 3-run window result
 */
export interface Rolling3RunWindowOffice {
  totalLineOfTrail: number;
  totalLoanBook: number;
  totalUpfrontNet: number;
  totalTrailNet: number;
  totalRecords: number;
  runs: any[];
  runsSummary: string;
  loading: boolean;
}

/**
 * Parameters for useRolling3RunWindowOffice hook
 */
export interface UseRolling3RunWindowOfficeParams {
  companyId: string | undefined;
  officeId?: string;
  templateId?: string;
  brokerId?: string;
  lender?: string;
  runCount?: number;
  yearMode: {
    year: number;
    yearType: 'calendar' | 'financial';
  };
  includeByBroker?: boolean;
  entityCode?: string;
}

interface BrokerBreakdown {
  brokerDisplay: string;
  totalLoanAmount: number;
  lineOfTrail: number;
  upfrontNet: number;
  trailNet: number;
  recordCount: number;
}

interface Rolling3RunResponse {
  totalLineOfTrail: number;
  totalLoanBook: number;
  totalUpfrontNet: number;
  totalTrailNet: number;
  totalRecords: number;
  months: Array<{
    month: string;
    loanBook: number;
    lineOfTrail: number;
    upfrontNet: number;
    trailNet: number;
    recordCount: number;
    byBroker?: Record<string, BrokerBreakdown>;
  }>;
}

/**
 * Office Dashboard hook for loan book and line of trail data
 * Uses year-based filtering only (calendar or financial year)
 */
export function useRolling3RunWindowOffice(params: UseRolling3RunWindowOfficeParams): Rolling3RunWindowOffice {
  const { companyId, officeId, templateId, brokerId, lender, yearMode, includeByBroker, entityCode } = params;

  const queryParams = {
    companyId: companyId ?? '',
    officeId,
    templateId,
    brokerId,
    lender,
    year: yearMode?.year,
    yearType: yearMode?.yearType,
    includeByBroker,
    entityCode,
  };
  const { data, loading } = useDashboardQuery<Rolling3RunResponse>(
    'getRolling3Run',
    queryParams,
    { enabled: !!companyId && !!yearMode }
  );

  // Resilience (bespoke-hook parity): once a window has loaded, never re-zero
  // the office loan-book / LOT tiles because `data` went undefined, whether
  // from a transient error OR a dropped/garbage-collected query result (two
  // simultaneous windows of the same callable in legacy mode). Hold last-good
  // and fall back regardless of error state; zero only when never loaded.
  // The fallback is scoped to the SAME params: a failed fetch for a different
  // entity/year must never paint the previous selection's figure (2026-10-09:
  // a rejected 2025 request kept showing the prior loan book tab's value).
  const paramsKey = JSON.stringify(queryParams);
  const lastGoodRef = useRef<{ key: string; data: Rolling3RunResponse } | undefined>(undefined);
  if (data) {
    lastGoodRef.current = { key: paramsKey, data };
  }
  const effectiveData =
    data ?? (lastGoodRef.current?.key === paramsKey ? lastGoodRef.current.data : undefined);

  return useMemo<Rolling3RunWindowOffice>(() => {
    if (!effectiveData) {
      return {
        totalLineOfTrail: 0,
        totalLoanBook: 0,
        totalUpfrontNet: 0,
        totalTrailNet: 0,
        totalRecords: 0,
        runs: [],
        runsSummary: '',
        loading,
      };
    }

    const runsSummary = effectiveData.months
      .map(run => run.month)
      .join(', ');

    return {
      totalLineOfTrail: effectiveData.totalLineOfTrail,
      totalLoanBook: effectiveData.totalLoanBook,
      totalUpfrontNet: effectiveData.totalUpfrontNet,
      totalTrailNet: effectiveData.totalTrailNet,
      totalRecords: effectiveData.totalRecords,
      runs: effectiveData.months,
      runsSummary,
      loading,
    };
  }, [effectiveData, loading]);
}

/**
 * Helper: Format loan book value (in cents) for display
 * @deprecated Import from '../../utils/formatters' instead
 */
export function formatLoanBook(value: number): string {
  return formatCompactCurrency(value);
}
